import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, BookOpen, Sprout } from 'lucide-react';
import { ModuleData, ModuleId, ModuleProgress } from '../../types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/Progress';
import * as Icons from 'lucide-react';
import { getModuleDisplayInfo } from '../../config/moduleContent';
import { GroupId } from '../../config/systemConfig';
interface ContinueLearningProps {
  modules: ModuleData[];
  progress: Record<ModuleId, ModuleProgress>;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  selectedGroup: GroupId;
}
export function ContinueLearning({
  modules,
  progress,
  onStartPractice,
  onNavigateToModules,
  selectedGroup
}: ContinueLearningProps) {
  // Filter modules that have been started
  const startedModules = modules.filter(m => {
    const modProgress = progress[m.id];
    return modProgress && (modProgress.questionsAnswered.length > 0 || Object.keys(modProgress.subSkills || {}).length > 0);
  }).sort((a, b) => {
    const progA = progress[a.id];
    const progB = progress[b.id];
    const lastA = Math.max(0, ...Object.values(progA.subSkills || {}).map(s => s.lastPracticed));
    const lastB = Math.max(0, ...Object.values(progB.subSkills || {}).map(s => s.lastPracticed));
    return lastB - lastA;
  }).slice(0, 2); // Show max 2 recent modules
  if (startedModules.length === 0) {
    return <div data-id="element-274">
        <div className="flex items-center gap-2 mb-4" data-id="element-275">
          <Play className="w-5 h-5 text-indigo-600 fill-indigo-600" data-id="element-276" />
          <h2 className="text-lg font-bold text-slate-900" data-id="element-277">Ga Verder</h2>
        </div>

        <Card className="p-8 text-center border-dashed border-2 border-slate-200 bg-slate-50/50 hover:bg-slate-50 hover:border-indigo-200 transition-all" data-id="element-278">
          <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center mx-auto mb-4 text-4xl ring-4 ring-slate-100" data-id="element-279">
            👋
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-280">
            Welkom bij Meester Djay!
          </h3>
          <p className="text-slate-500 max-w-md mx-auto mb-6" data-id="element-281">
            Je hebt nog geen oefeningen gemaakt. Kies een vak om te beginnen met
            oefenen.
          </p>
          <Button onClick={onNavigateToModules} size="lg" data-id="element-282">
            <BookOpen className="w-5 h-5 mr-2" data-id="element-283" />
            Kies je eerste vak
          </Button>
        </Card>
      </div>;
  }
  return <div data-id="element-284">
      <div className="flex items-center justify-between mb-4" data-id="element-285">
        <div className="flex items-center gap-2" data-id="element-286">
          <Play className="w-5 h-5 text-indigo-600 fill-indigo-600" data-id="element-287" />
          <h2 className="text-lg font-bold text-slate-900" data-id="element-288">Ga Verder</h2>
        </div>
        <button onClick={onNavigateToModules} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors px-3 py-1 rounded-lg hover:bg-indigo-50" data-id="element-289">
          Alle vakken <ArrowRight className="w-4 h-4" data-id="element-290" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-291">
        {startedModules.map(module => {
        const modProgress = progress[module.id];
        const IconComponent = (Icons as any)[module.icon] || Icons.HelpCircle;
        const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
        const title = displayInfo?.title || module.title;
        // Safe access to subSkills
        const subSkills = modProgress.subSkills || {};
        const activeSkills = Object.values(subSkills).length;
        const bgColorClass = module.color.replace('text-', 'bg-').replace('500', '50');
        return <motion.div key={module.id} whileHover={{
          y: -2
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20
        }} data-id="element-292">
              <Card className="h-full p-6 flex flex-col border-slate-200/60 hover:border-indigo-200 transition-colors shadow-sm hover:shadow-md" data-id="element-293">
                <div className="flex items-start justify-between mb-6" data-id="element-294">
                  <div className="flex items-center gap-4" data-id="element-295">
                    <div className={`p-3.5 rounded-xl ${bgColorClass}`} data-id="element-296">
                      <IconComponent className={`w-7 h-7 ${module.color}`} data-id="element-297" />
                    </div>
                    <div data-id="element-298">
                      <h3 className="font-bold text-lg text-slate-900" data-id="element-299">
                        {title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-slate-500 font-medium mt-1" data-id="element-300">
                        <Sprout className="w-3.5 h-3.5 text-emerald-500" data-id="element-301" />
                        <span data-id="element-302">{activeSkills} skills actief</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-auto space-y-5" data-id="element-303">
                  <div data-id="element-304">
                    <div className="flex justify-between text-xs font-medium text-slate-500 mb-2" data-id="element-305">
                      <span data-id="element-306">Beheersing</span>
                      <span className="text-slate-900 font-bold" data-id="element-307">
                        {Math.round(modProgress.masteryPercentage)}%
                      </span>
                    </div>
                    <ProgressBar value={modProgress.masteryPercentage} height={8} color={modProgress.masteryPercentage >= 95 ? 'bg-emerald-500' : 'bg-indigo-600'} data-id="element-308" />
                  </div>

                  <Button onClick={() => onStartPractice(module.id)} fullWidth variant="primary" className="justify-between group" data-id="element-309">
                    <span data-id="element-310">Verder Oefenen</span>
                    <Play className="w-4 h-4 fill-current opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-311" />
                  </Button>
                </div>
              </Card>
            </motion.div>;
      })}
      </div>
    </div>;
}